'use client';
import type React from 'react';
import dataMindLogo from '@/assets/images/data-mind-logo.svg';

/**
 * ChartPopover
 *
 * A floating tooltip-like popover component that displays sensor values associated with a timestamp.
 * It appears near the user's cursor on chart interaction, and includes automatic repositioning to
 * remain within the viewport.
 *
 * @component
 *
 * @param {ChartPopoverProps} props - Props to control the visibility, position, and content of the popover.
 * @param {boolean} props.isVisible - Determines whether the popover should be rendered.
 * @param {{ x: number; y: number }} props.position - The screen coordinates (usually cursor) where the popover should appear.
 * @param {{
 *   timestamp: string;
 *   values: Array<{
 *     name: string;
 *     value: number;
 *     color: string;
 *     unit: string;
 *     tooltipData?: Record<string, any>;
 *   }>;
 * } | null} props.data - Data payload to display. If `null`, the popover is hidden.
 * @param {boolean} [props.showTooltipData=false] - Whether to show additional tooltip data.
 * @param {(tooltipData: Record<string, any>, sensorName: string) => React.ReactNode} [props.renderTooltipData] - Custom render function for tooltip data.
 *
 * @returns {JSX.Element | null} The positioned popover with sensor data, or `null` if hidden.
 *
 * @example
 * <ChartPopover
 *   isVisible={hoverActive}
 *   position={{ x: mouseX, y: mouseY }}
 *   data={{
 *     timestamp: '2025-07-28T12:30:00Z',
 *     values: [
 *       { name: 'Sensor A', value: 1.23, color: '#00f', unit: 'mm/s' },
 *       { name: 'Sensor B', value: 0.87, color: '#0f0', unit: 'g' },
 *     ]
 *   }}
 * />
 */

export const ChartPopover: React.FC<ChartPopoverProps> = ({ 
  isVisible, 
  position, 
  data, 
  showTooltipData = false,
  renderTooltipData 
}) => {
  if (!isVisible || !data) {
    return null;
  }

  // Format timestamp - handle both dates and numeric values
  const formatTimestamp = (timestamp: string) => {
    // Check if it's a numeric value (like frequency in Hz)
    const numValue = Number(timestamp);
    if (!isNaN(numValue) && isFinite(numValue)) {
      // Check if it looks like a Unix timestamp (very large number) or a date string
      const date = new Date(timestamp);
      const isValidDate = date instanceof Date && !isNaN(date.getTime());

      // If the numeric value is small (< 10000) or doesn't produce a valid date, treat as numeric
      if (numValue < 10000 || !isValidDate || date.getFullYear() < 1970) {
        // Format as actual number with 1 decimal point
        return `${numValue.toFixed(1)}`;
      }
    }

    // Otherwise format as date
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  // Calculate position to keep popover within viewport and close to cursor
  const calculatePosition = () => {
    const popoverWidth = showTooltipData ? 250 : 200; // Increased width only if showing tooltip data
    const hasTooltipData = showTooltipData && data.values.some(v => v.tooltipData);
    const baseHeight = 100;
    const itemHeight = hasTooltipData ? 80 : 24; // More height if tooltip data exists and is shown
    const popoverHeight = baseHeight + data.values.length * itemHeight;
    const padding = 10;
    const offset = 15; // Small offset from cursor

    let x = position.x + offset;
    let y = position.y - offset - 100;

    // Keep within viewport bounds
    if (typeof window !== 'undefined') {
      // If popover would go off right edge, show on left side of cursor
      if (x + popoverWidth > window.innerWidth - padding - 200) {
        x = position.x - popoverWidth - offset;
      }

      // If popover would go off bottom edge, show above cursor
      if (y + popoverHeight > window.innerHeight - padding) {
        y = position.y - popoverHeight - offset;
      }

      // If popover would go off top edge, show below cursor
      if (y < padding) {
        y = position.y + offset;
      }

      // If popover would go off left edge, show on right side
      if (x < padding) {
        x = position.x + offset;
      }
    }

    return { x, y };
  };

  const adjustedPosition = calculatePosition();

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      {showTooltipData && renderTooltipData && data.values.some(v => v.tooltipData) ? (
        // Custom alarm tooltip rendering
        <div className="bg-white dark:bg-[#52606d] border border-gray-200 dark:border-[#2f3844] rounded-lg shadow-lg p-3 opacity-95">
          {renderTooltipData(
            data.values[0]?.tooltipData || {}, 
            data.values[0]?.name || '', 
            data.values,
            data.timestamp
          )}
        </div>
      ) : (
        // Default popover rendering
        <div className="bg-white dark:bg-[#52606d] border border-gray-200 dark:border-[#2f3844] rounded-lg shadow-lg p-3 min-w-[200px] max-w-[300px] opacity-90">
          {/* Timestamp Header */}
          <div className="text-xs font-medium text-gray-600 dark:text-[#8b98a5] pb-2 mb-2 border-b border-gray-100 dark:border-[#2f3844] flex justify-between">
            {formatTimestamp(data.timestamp)} <img src={dataMindLogo} className="h-3" />
          </div>

          {/* Sensor Values */}
          <div className="space-y-1.5 ">
            {[...data.values]
              .sort((a, b) => b.value - a.value)
              .map((sensor, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {/* Color dot */}
                      <div className="w-[0.3rem] h-4 flex-shrink-0" style={{ backgroundColor: sensor.color }} />
                      {/* Sensor name */}
                      <span className="text-gray-700 dark:text-[#e1e8ed] truncate font-medium text-sm">{sensor.name}</span>
                    </div>
                    {/* Value */}
                    <div className="text-gray-900 dark:text-gray-100 font-semibold ml-2">
                      {sensor.value.toFixed(2)}
                      {sensor.unit && (
                        <span className="text-gray-900 dark:text-gray-100 ml-0.5 font-normal text-sm">{` [${sensor.unit}] `}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Additional Tooltip Data (for default rendering only) */}
                  {showTooltipData && !renderTooltipData && sensor.tooltipData && Object.keys(sensor.tooltipData).length > 0 && (
                    <div className="mt-1 pl-7 text-[10px] space-y-0.5">
                      {Object.entries(sensor.tooltipData).map(([key, value]) => {
                        // Skip null or undefined values
                        if (value === null || value === undefined) return null;
                        
                        // Format the key for display
                        const displayKey = key
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (l) => l.toUpperCase());
                        
                        // Determine text color based on key or value
                        let valueClassName = 'text-gray-700 dark:text-gray-300';
                        if (key === 'level') {
                          if (value === 'critical') {
                            valueClassName = 'text-red-600 dark:text-red-400 font-medium';
                          } else if (value === 'non_critical') {
                            valueClassName = 'text-yellow-600 dark:text-yellow-400 font-medium';
                          }
                        }
                        
                        // Format the value for display
                        const displayValue = typeof value === 'string' 
                          ? value.replace(/_/g, ' ')
                          : typeof value === 'boolean'
                          ? value ? 'Yes' : 'No'
                          : typeof value === 'number'
                          ? value.toLocaleString()
                          : String(value);
                        
                        return (
                          <div key={key} className="flex items-center gap-1">
                            <span className="text-gray-500 dark:text-gray-400">{displayKey}:</span>
                            <span className={valueClassName}>{displayValue}</span>
                          </div>
                        );
                      }).filter(Boolean)}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface ChartPopoverProps {
  isVisible: boolean;
  position: { x: number; y: number };
  data: {
    timestamp: string;
    values: Array<{
      name: string;
      value: number;
      color: string;
      unit: string;
      tooltipData?: Record<string, any>;
    }>;
  } | null;
  showTooltipData?: boolean;
  renderTooltipData?: (tooltipData: Record<string, any>, sensorName: string, allSensorData?: Array<any>, timestamp?: string) => React.ReactNode;
}
